import { Phone, Mail, MapPin, Instagram, Facebook, Cookie } from 'lucide-react';
import { Link } from 'react-router-dom';
import { openCookieSettings } from './CookieConsent';

const LOGO_URL = "/images/93q1mmpr_fesch.png";

const footerLinks = [
  { name: 'Startseite', href: '#hero' },
  { name: 'Leistungen', href: '#leistungen' },
  { name: 'Über uns', href: '#ueber-uns' },
  { name: 'Referenzen', href: '#referenzen' },
  { name: 'Kontakt', href: '#kontakt' },
];

const legalLinks = [
  { name: 'Impressum', href: '/impressum' },
  { name: 'Datenschutz', href: '/datenschutzerklarung' },
];

export const Footer = () => {
  const currentYear = new Date().getFullYear();

  const scrollToSection = (e, href) => {
    if (href.startsWith('#')) {
      e.preventDefault();
      const element = document.querySelector(href);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  };

  return (
    <footer data-testid="footer" className="bg-[#0A0A0A]">
      {/* Red Line */}
      <div className="footer-red-line" />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Company Info */}
          <div className="lg:col-span-2">
            <img 
              src={LOGO_URL} 
              alt="Fesch Elektrotechnik Logo" 
              className="h-12 mb-6 brightness-0 invert"
            />
            <p className="text-white/70 mb-6 max-w-md leading-relaxed">
              Ihr zuverlässiger Partner für Elektrotechnik in Wuppertal und Umgebung. 
              Qualität, Sicherheit und Kundenzufriedenheit stehen bei uns an erster Stelle.
            </p>
            
            {/* Contact Info */}
            <div className="space-y-3">
              <a 
                href="tel:020243044510" 
                className="flex items-center gap-3 text-white/70 hover:text-[#E31E24] transition-colors"
              >
                <Phone className="w-4 h-4" />
                <span>0202 / 430 445 10</span>
              </a>
              <a 
                href="mailto:info@fesch-elektrotechnik.de" 
                className="flex items-center gap-3 text-white/70 hover:text-[#E31E24] transition-colors"
              >
                <Mail className="w-4 h-4" />
                <span>info@fesch-elektrotechnik.de</span>
              </a>
              <div className="flex items-center gap-3 text-white/70">
                <MapPin className="w-4 h-4 flex-shrink-0" />
                <span>An der Bergbahn 2a, 42289 Wuppertal</span>
              </div>
            </div>
          </div>

          {/* Navigation */}
          <div>
            <h4 className="text-white font-bold text-lg mb-6">Navigation</h4>
            <nav className="space-y-3">
              {footerLinks.map((link) => (
                <a
                  key={link.name}
                  href={link.href}
                  onClick={(e) => scrollToSection(e, link.href)}
                  className="block footer-link"
                >
                  {link.name}
                </a>
              ))}
            </nav>
          </div>

          {/* Legal & Social */}
          <div>
            <h4 className="text-white font-bold text-lg mb-6">Rechtliches</h4>
            <nav className="space-y-3 mb-8">
              {legalLinks.map((link) => (
                <Link
                  key={link.name}
                  to={link.href}
                  className="block footer-link"
                >
                  {link.name}
                </Link>
              ))}
              <button
                onClick={openCookieSettings}
                data-testid="footer-cookie-settings"
                className="flex items-center gap-2 footer-link text-left"
              >
                <Cookie className="w-4 h-4" />
                Cookie-Einstellungen
              </button>
            </nav>

            <h4 className="text-white font-bold text-lg mb-4">Folgen Sie uns</h4>
            <div className="flex gap-3">
              <a
                href="https://www.instagram.com/fesch.elektrotechnik"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-instagram"
                className="w-10 h-10 border border-white/30 flex items-center justify-center text-white/70 hover:bg-[#E31E24] hover:border-[#E31E24] hover:text-white transition-all"
                aria-label="Instagram"
              >
                <Instagram className="w-5 h-5" />
              </a>
              <a
                href="https://www.facebook.com"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-facebook"
                className="w-10 h-10 border border-white/30 flex items-center justify-center text-white/70 hover:bg-[#E31E24] hover:border-[#E31E24] hover:text-white transition-all"
                aria-label="Facebook"
              >
                <Facebook className="w-5 h-5" />
              </a>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-white/10 mt-12 pt-8 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-white/50 text-sm">
            © {currentYear} FESCH Elektrotechnik GmbH. Alle Rechte vorbehalten.
          </p>
          <div className="flex items-center gap-4 text-white/50 text-sm">
            <span>Handwerkskammer Düsseldorf</span>
            <span>•</span>
            <span>HRB 33460</span>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
